import { sha256 } from '@noble/hashes/sha256';
import { utf8ToBytes } from '@noble/hashes/utils';
import { z } from 'zod';

export const moderationDecisionSchema = z.enum(['allow', 'shadow', 'block']);
export type ModerationDecision = z.infer<typeof moderationDecisionSchema>;

export const moderationCategorySchema = z.enum([
  'sexual',
  'nudity',
  'sexual/minors',
  'harassment',
  'harassment/threatening',
  'hate',
  'hate/threatening',
  'illicit',
  'illicit/violent',
  'self-harm',
  'self-harm/intent',
  'self-harm/instructions',
  'violence',
  'violence/graphic'
]);
export type ModerationCategory = z.infer<typeof moderationCategorySchema>;

export const moderationSummarySchema = z.object({
  decision: moderationDecisionSchema,
  flagged: z.boolean(),
  sources: z.array(z.string()).default([]),
  reasons: z.array(z.string()).default([]),
  categories: z.record(z.boolean()).optional(),
  categoryScores: z.record(z.number()).optional(),
  eventId: z.string().optional(),
  evaluatedAt: z.string().datetime(),
  policy: z
    .object({
      name: z.string(),
      version: z.string()
    })
    .optional()
});
export type ModerationSummary = z.infer<typeof moderationSummarySchema>;

export type ModerationThresholds = Partial<Record<ModerationCategory, number>>;

export interface ModerationPolicy {
  /**
   * When OpenAI returns `flagged: true`, treat it as this decision. Defaults to `block`.
   */
  flaggedDecision?: ModerationDecision;
  /**
   * Categories that should result in a `block` verdict when the score meets or exceeds the provided threshold.
   */
  blockThresholds?: ModerationThresholds;
  /**
   * Categories that should result in a `shadow` verdict (allow but mark for review) when the score meets or exceeds the provided threshold.
   */
  shadowThresholds?: ModerationThresholds;
  /**
   * Name + version surfaced in telemetry/audit logs so we know which config produced a verdict.
   */
  descriptor?: {
    name: string;
    version: string;
  };
}

export interface EvaluateModerationInput {
  flagged: boolean;
  categories: Partial<Record<ModerationCategory, boolean>>;
  categoryScores: Partial<Record<ModerationCategory, number>>;
  policy?: ModerationPolicy;
}

export interface EvaluateModerationOutput {
  decision: ModerationDecision;
  flagged: boolean;
  reasons: string[];
  matchedCategories: ModerationCategory[];
  policyDescriptor?: ModerationPolicy['descriptor'];
}

const defaultPolicy: Required<Pick<ModerationPolicy, 'flaggedDecision'>> = {
  flaggedDecision: 'block'
};

const collectMatches = (
  scores: Partial<Record<ModerationCategory, number>>,
  thresholds: ModerationThresholds | undefined
) => {
  if (!thresholds) {
    return new Set<ModerationCategory>();
  }
  const matched = new Set<ModerationCategory>();
  for (const [category, threshold] of Object.entries(thresholds) as Array<[ModerationCategory, number]>) {
    const score = scores[category];
    if (typeof score === 'number' && score >= threshold) {
      matched.add(category);
    }
  }
  return matched;
};

export const evaluateModeration = (input: EvaluateModerationInput): EvaluateModerationOutput => {
  const policy = input.policy ?? {};
  const flaggedDecision = policy.flaggedDecision ?? defaultPolicy.flaggedDecision;
  let decision: ModerationDecision = 'allow';
  const reasons: string[] = [];

  if (input.flagged) {
    decision = flaggedDecision;
    reasons.push('provider.flagged');
  }

  const blockMatches = collectMatches(input.categoryScores, policy.blockThresholds);

  if (decision !== 'block' && blockMatches.size > 0) {
    decision = 'block';
    for (const category of blockMatches) {
      reasons.push(`threshold.block.${category}`);
    }
  }

  const shadowMatches = collectMatches(input.categoryScores, policy.shadowThresholds);

  if (decision === 'allow' && shadowMatches.size > 0) {
    decision = 'shadow';
    for (const category of shadowMatches) {
      reasons.push(`threshold.shadow.${category}`);
    }
  }

  return {
    decision,
    flagged: input.flagged,
    reasons,
    matchedCategories: [...new Set<ModerationCategory>([...blockMatches, ...shadowMatches])],
    policyDescriptor: policy.descriptor
  };
};

export const hashString = (...parts: Array<string | number | boolean | null | undefined>) => {
  const normalized = parts
    .map((part) => (part === undefined || part === null ? '' : typeof part === 'string' ? part : String(part)))
    .join('|');
  const digest = sha256(utf8ToBytes(normalized));
  return Array.from(digest)
    .map((byte) => byte.toString(16).padStart(2, '0'))
    .join('');
};

export const hashSafetyIdentifier = (value: string, salt?: string) =>
  salt ? hashString(salt, value) : hashString(value);
